import { useEffect, useState } from "react";
import { X } from "lucide-react";
import SchoolTypeahead from "./SchoolTypeahead";
import { toSchoolInput } from "./wizardData";
import { OnboardingQuestion } from "./OnboardingQuestion";
import type { SchoolResult } from "@/services/schools";
import type { OnboardingSchoolInput } from "@/services/pipeline";
import type { OnboardingStepProps } from "./types";
import { DreamSchoolLogoCollage } from "./DreamSchoolLogoCollage";

const MAX_PICKS = 3;

/**
 * Step 3 — optional dream school picks (up to 3). Picks are not saved to the
 * profile; they flow into the wizard's accumulated data and are sent to
 * `generateRecruitingPlan` when onboarding finishes. Always valid.
 */
export default function DreamSchoolsStep({
  profile,
  onValidityChange,
  registerSave,
  setWizardData,
  wizardData,
}: OnboardingStepProps) {
  const [picks, setPicks] = useState<OnboardingSchoolInput[]>(wizardData?.dreamSchools ?? []);

  // Optional step: always valid. Data is pushed eagerly via setWizardData, so
  // no save fn is needed — Continue just advances.
  useEffect(() => {
    onValidityChange(true);
    registerSave(null);
    return () => registerSave(null);
  }, [onValidityChange, registerSave]);

  const updatePicks = (next: OnboardingSchoolInput[]) => {
    setPicks(next);
    setWizardData({ dreamSchools: next });
  };

  const handleSelect = (school: SchoolResult) => {
    if (picks.length >= MAX_PICKS) return;
    if (picks.some(p => p.teamId === school.schoolId)) return;
    updatePicks([...picks, toSchoolInput(school)]);
  };

  const handleRemove = (teamId: string) => {
    updatePicks(picks.filter(p => p.teamId !== teamId));
  };

  return (
    <OnboardingQuestion
      eyebrow="Dream schools"
      title="Which schools are on the dream list?"
      description="Add up to three schools. It’s okay to aim big—these show me what feels exciting."
      coachCopy="Dream schools are a starting point, not a prediction. I’ll build a balanced list around them."
      coachBadge="✦"
    >
      <DreamSchoolLogoCollage gender={profile.gender} />

      {picks.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {picks.map(p => (
            <span
              key={p.teamId}
              className="inline-flex items-center gap-2 rounded-full border border-border-strong bg-white px-4 py-2 text-xs font-extrabold text-forest shadow-sm"
            >
              {p.schoolName}
              <button
                type="button"
                aria-label={`Remove ${p.schoolName}`}
                onClick={() => handleRemove(p.teamId)}
                className="text-muted-foreground transition-colors hover:text-forest"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {picks.length < MAX_PICKS ? (
        <SchoolTypeahead
          onSelect={handleSelect}
          excludeTeamIds={picks.map(p => p.teamId)}
          gender={profile.gender}
        />
      ) : (
        <p className="rounded-2xl border border-border bg-white px-4 py-3 text-xs font-semibold text-muted-foreground">
          That&apos;s 3 — a great start. Remove one to swap in a different school.
        </p>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        Don&apos;t have a dream school yet? Skip this and I&apos;ll help you discover some.
      </p>
    </OnboardingQuestion>
  );
}
